import React, {useEffect} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import _ from "lodash";
import {z} from "zod";
import {v4 as uuidv4} from "uuid";
import dayjs, {Dayjs} from "dayjs";
import {Trans, useTranslation} from "react-i18next";
import {Alert, Avatar, Box, Grid, Stack, Typography} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {swallowException} from "../../utils/appUtils";
import {Order, PatchOrderBodyType, PostOrderBodyType} from "../../types/domain/order";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {TextInput} from "../base/TextInput";
import {Field} from "../../types/domain/field";
import {Dialog} from "../base/Dialog";
import {LabeledInput} from "../base/LabeledInput";
import {FieldsTableHeader} from "../fields/FieldsTableHeader";
import {OrderEditFieldsTable} from "../orders/OrderEditFieldsTable";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {DatePickerInput} from "../base/DatePickerInput";
import {nextYearMinusDay} from "../../utils/dateUtils";
import {TextFieldFramed} from "../base/TextFieldFramed";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean, companyResponse?: Order) => void;
    mode: "new" | "edit";
    companyId: string;
    selectedOrder?: PatchOrderBodyType & {id: string; areaInHa?: number};
    setOrder?: React.Dispatch<React.SetStateAction<{id: string} | undefined>>;
}

function getOrderId(mode: string, order?: {id: string}) {
    if (mode === "edit") {
        return order!.id;
    } else {
        return uuidv4();
    }
}

export const OrderCreateDialog: React.FC<Props> = ({open, onClose, mode, companyId, selectedOrder, setOrder}) => {
    const {t} = useTranslation();
    const orderManagement = useOrderManagement();
    const orderId = getOrderId(mode, selectedOrder);
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const constructValues = (order: PatchOrderBodyType & {id: string; areaInHa?: number}): FormInputs => {
        return {
            ...order,
            startDate: dayjs(order.startDate),
            endDate: dayjs(order.endDate),
        };
    };
    const initFormValues = mode === "edit" && selectedOrder ? constructValues(selectedOrder) : INITIAL_FORM_VALUES;
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        register,
        formState: {errors},
    } = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(ORDER_CREATE_SCHEMA),
    });
    // eslint-disable-next-line react-hooks/incompatible-library
    const startDate = watch("startDate");
    const price = watch("price");
    const discount = watch("discount");

    useEffect(() => {
        if (mode === "new" && startDate) {
            const endDate = nextYearMinusDay(startDate);
            setValue("endDate", endDate);
        }
    }, [startDate, setValue, mode]);

    useEffect(() => {
        const discountFraction = discount / 100;
        const finalPrice = Math.max(0, _.round(price - price * discountFraction, 2));
        setValue("finalPrice", finalPrice);
    }, [price, setValue, discount]);

    const onDelete = () => {
        orderManagement.reset();

        orderManagement
            .delete(companyId, selectedOrder!.id)
            .then(() => {
                onClose(true);
            })
            .catch(swallowException);
    };
    const onSubmit = (data: FormInputs) => {
        orderManagement.reset();
        if (mode === "new") {
            const orderBody = formInputsToPostOrderBody(data, orderId);
            orderManagement
                .create(companyId, orderBody)
                .then((response) => {
                    if (setOrder) {
                        setOrder({id: response.id});
                    }
                    onClose(true);
                })
                .catch(swallowException);
        } else {
            const orderBody = formInputsToPatchOrderBody(data);
            orderManagement
                .edit(companyId, selectedOrder!.id, orderBody)
                .then(() => {
                    onClose(true);
                })
                .catch(swallowException);
        }
    };

    const fieldHeaderItems = [
        t("orders.order_create.header_field"),
        t("orders.order_create.header_ready"),
        t("orders.order_create.header_crop"),
        t("orders.order_create.header_area"),
    ];

    const header = <FieldsTableHeader items={fieldHeaderItems}></FieldsTableHeader>;

    return (
        <Dialog
            enableSubmit
            enableDelete={mode === "edit"}
            deleteTitle={t("dialogs.actions.delete")}
            submitTitle={t("dialogs.actions.save")}
            title={mode === "edit" ? t("orders.order_create.edit_title") : t("orders.order_create.create_title")}
            titleIcon={<FolderOpenIcon color="primary" />}
            open={open}
            deleteConfirmation={t("orders.order_create.delete_confirmation")}
            disableDefaultClose={true}
            onClose={() => onClose(false)}
            onDelete={onDelete}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit)}
        >
            <Stack direction="column" useFlexGap>
                <form>
                    {isResourceFailed(orderManagement.resource) && (
                        <Alert severity="error">{getResourceErrorMessage(orderManagement.resource)}</Alert>
                    )}

                    <Stack direction="column" spacing={2} useFlexGap>
                        <Grid item xs={12} rowGap={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <LabeledInput label={t("orders.order_create.order_nb")}>
                                        <TextFieldFramed value={`#${orderId.slice(0, 8)}`}></TextFieldFramed>
                                    </LabeledInput>
                                </Grid>
                            </Grid>
                            {access.canViewOrderPrice && (
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("orders.order_create.price")}>
                                            <TextInput
                                                disabled={!access.canViewOrderPrice}
                                                name="price"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("orders.order_create.discount") + " %"}>
                                            <TextInput
                                                disabled={!access.canViewOrderPrice}
                                                name="discount"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("orders.order_create.final_price")}>
                                            <TextInput
                                                disabled={!access.canViewOrderPrice}
                                                name="finalPrice"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("orders.order_create.subscription_start")}>
                                            <DatePickerInput name="startDate" control={control} errors={errors} />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("orders.order_create.subscription_end")}>
                                            <DatePickerInput name="endDate" control={control} errors={errors} />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Box border={1} borderColor="secondary.main">
                            {mode === "edit" ? (
                                <OrderEditFieldsTable orderId={selectedOrder!.id} companyId={companyId} />
                            ) : (
                                <div>
                                    {header}
                                    <Box bgcolor="white" p={4}>
                                        <Stack
                                            spacing={2}
                                            direction="column"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Avatar
                                                variant="circular"
                                                sx={{
                                                    bgcolor: "#F2F4F7",
                                                    width: "2.5rem",
                                                    height: "2.5rem",
                                                }}
                                            >
                                                <InfoIcon color="disabled" />
                                            </Avatar>
                                            <Typography width="23rem" color="#656B80">
                                                <Trans i18nKey="orders.order_create.info_message"></Trans>
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </div>
                            )}
                        </Box>
                    </Stack>
                </form>
            </Stack>
        </Dialog>
    );
};

interface FormInputs {
    price: number;
    discount: number;
    finalPrice: number;
    areaInHa?: number;
    startDate: Dayjs;
    endDate: Dayjs;
    fields?: Field[];
}

const INITIAL_FORM_VALUES: FormInputs = {
    price: 0,
    discount: 0,
    finalPrice: 0,
    startDate: dayjs(),
    endDate: nextYearMinusDay(dayjs()),
    areaInHa: 0,
    fields: [],
};

const ORDER_CREATE_SCHEMA = z.object({
    price: z.number().nonnegative(),
    discount: z.number().nonnegative().min(0).max(100),
    finalPrice: z.number().nonnegative(),
    startDate: z.instanceof(dayjs as unknown as typeof Dayjs),
    endDate: z.instanceof(dayjs as unknown as typeof Dayjs),
});

const formInputsToPatchOrderBody = (inputs: FormInputs) => {
    const subscriptionType = "Standart";
    const order: PatchOrderBodyType = {
        price: inputs.price,
        subscriptionType,
        discount: inputs.discount,
        finalPrice: inputs.finalPrice,
        startDate: inputs.startDate.format("YYYY-MM-DD"),
        endDate: inputs.endDate.format("YYYY-MM-DD"),
    };
    return order;
};

const formInputsToPostOrderBody = (inputs: FormInputs, id: string) => {
    const subscriptionType = "Standart";

    const order: PostOrderBodyType = {
        id,
        price: inputs.price,
        subscriptionType,
        discount: inputs.discount,
        finalPrice: inputs.finalPrice,
        startDate: inputs.startDate.format("YYYY-MM-DD"),
        endDate: inputs.endDate.format("YYYY-MM-DD"),
    };
    return order;
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canViewOrderPrice"]);
